import React, { useEffect, useRef, useState } from "react";
import type { FocusEvent, KeyboardEvent } from "react";
import { FaTimes } from "react-icons/fa";
import U2000InvalidFeedback from "../feedback/U2000InvalidFeedback";
import U2000Label from "../label/U2000Label";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";
import { getNestedValue, setNestedValue } from "../../../tools/U2000ObjectUtils";

export interface U2000InputTagBaseProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  label: string;
  readonly?: boolean;
  required?: boolean;
  icon?: React.ReactNode;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  formatter?: (val: string) => string;
  placeholder?: string;
  filterFunction: (value: string) => string;
  /** Contenu à droite du champ tags, aligné verticalement au centre avec l’input. */
  inlineEnd?: React.ReactNode;
}

// Classes CSS modernes pour les tags
const getTagClasses = (readonly?: boolean) => {
  const baseClasses = "inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-150 max-w-xs";

  if (readonly) {
    return `${baseClasses} bg-muted text-gray-600 border border-gray-200`;
  }

  return `${baseClasses} bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100`;
};

// Classes pour le conteneur principal
const getContainerClasses = (formStateHandlers: U2000FormStateHandlers<any>, fieldName: string, isFocused: boolean, readonly?: boolean) => {
  // BL-0349 : un seul fond par état (pas bg-surface + bg-muted)
  const baseClasses = "flex flex-wrap items-center gap-1.5 w-full min-h-[46px] px-4 py-2 text-sm border rounded-lg transition-all duration-200";
  const isDisabled = readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const hasError = (formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName]) || (formStateHandlers.isSubmitted() && formStateHandlers.getErrors()[fieldName]);

  if (isDisabled) {
    return `${baseClasses} bg-muted text-gray-500 border-gray-200 cursor-not-allowed`;
  }

  if (hasError) {
    return `${baseClasses} bg-surface border-red-300 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/20`;
  }

  if (isFocused) {
    return `${baseClasses} bg-surface border-blue-500 ring-2 ring-blue-500/20`;
  }

  return `${baseClasses} bg-surface border-gray-300 hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20`;
};

const U2000InputTagBase: React.FC<U2000InputTagBaseProps> = ({
  formStateHandlers,
  fieldName,
  label,
  readonly = false,
  required = false,
  icon,
  onBlur,
  formatter = null,
  placeholder = "",
  filterFunction,
  inlineEnd,
}) => {
  const tags: string[] = getNestedValue(formStateHandlers.getData(), fieldName) || [];
  const [inputValue, setInputValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);
  const inputTextTagRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Gère la perte de focus de l'input :
   * - Nettoie la valeur saisie
   * - Ajoute le tag si non vide et non dupliqué
   * - Déclenche la validation et le marquage du champ comme touché
   */
  function handleBlur(e: FocusEvent<HTMLInputElement>) {
    setIsFocused(false);

    let cleaned = inputValue.trim().replace(/^[,;\s]+|[,;\s]+$/g, "");
    cleaned = filterFunction(cleaned);

    if (formatter && typeof formatter === "function") {
      cleaned = cleaned !== "" ? formatter(cleaned) : "";
    }

    if (cleaned !== "" && !tags.includes(cleaned)) {
      const newTags = [...tags, cleaned];
      const currentValue = formStateHandlers.getData();
      setNestedValue(currentValue, fieldName, newTags);
      formStateHandlers.setData({ ...currentValue });
    }

    setInputValue("");

    if (onBlur) onBlur(e);
    if (formStateHandlers.onBlur) formStateHandlers.onBlur(e);
  }

  /**
   * Met à jour la valeur de l'input en filtrant selon le type autorisé.
   */
  function handleInputChange(value: string) {
    const filtered = filterFunction(value);
    setInputValue(filtered);
  }

  /**
   * Gère les touches clavier sur l'input :
   * - Entrée ou virgule : ajoute le tag si valide
   * - Backspace à vide : supprime le dernier tag
   */
  function handleInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if ((e.key === "Enter" || e.key === ",") && inputValue.trim() !== "") {
      e.preventDefault();

      let cleaned = inputValue.trim().replace(/^[,;\s]+|[,;\s]+$/g, "");
      cleaned = filterFunction(cleaned);

      if (formatter && typeof formatter === "function") {
        cleaned = cleaned !== "" ? formatter(cleaned) : "";
      }

      if (cleaned !== "" && !tags.includes(cleaned)) {
        const newTags = [...tags, cleaned];
        const currentValue = formStateHandlers.getData();
        setNestedValue(currentValue, fieldName, newTags);
        formStateHandlers.setData({ ...currentValue });
      }

      setInputValue("");
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      e.preventDefault();
      const newTags = tags.slice(0, -1);
      const currentValue = formStateHandlers.getData();
      setNestedValue(currentValue, fieldName, newTags);
      formStateHandlers.setData({ ...currentValue });
    }
  }

  /**
   * Supprime le tag à l'index donné.
   */
  function handleRemoveTag(index: number) {
    const newTags = tags.filter((_, i) => i !== index);
    const currentValue = formStateHandlers.getData();
    setNestedValue(currentValue, fieldName, newTags);
    formStateHandlers.setData({ ...currentValue });

    // Redonner le focus à l'input après suppression
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }

  // Gestion du clic à l'extérieur
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (inputTextTagRef.current && !inputTextTagRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus sur l'input quand le conteneur est cliqué
  const handleContainerClick = () => {
    if (!readonly && formStateHandlers.getFormState() !== U2000FormStateEnumState.READ) {
      inputRef.current?.focus();
      setIsFocused(true);
    }
  };

  const isDisabled = readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ;

  const tagInputRow = (
    <div className="relative">
      {icon && <div className="absolute left-3 top-3 text-gray-400 pointer-events-none z-10">{icon}</div>}

      <div
        ref={inputTextTagRef}
        className={`${getContainerClasses(formStateHandlers, fieldName, isFocused, readonly)} ${icon ? "pl-10" : ""}`}
        onClick={handleContainerClick}
        role="textbox"
        aria-label={`Tags pour ${label}`}
        aria-invalid={!!(formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName])}
        aria-describedby={formStateHandlers.getErrors()[fieldName] ? `${fieldName}-error` : undefined}
      >
        {tags.map((tag, idx) => (
          <span key={idx} className={getTagClasses(readonly)}>
            <span className="truncate max-w-[150px]" title={tag}>
              {tag}
            </span>
            {!isDisabled && (
              <button
                type="button"
                className="ml-1.5 text-current hover:text-red-600 focus:outline-hidden focus:text-red-600 transition-colors duration-150 rounded-full p-0.5 hover:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveTag(idx);
                }}
                aria-label={`Supprimer le tag ${tag}`}
              >
                <FaTimes className="w-2.5 h-2.5" />
              </button>
            )}
          </span>
        ))}

        {!isDisabled && (
          <input
            ref={inputRef}
            type="text"
            id={fieldName}
            name={fieldName}
            className="flex-1 bg-transparent text-sm border-none outline-hidden min-w-[80px] placeholder:text-gray-400"
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleInputKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            placeholder={tags.length === 0 ? placeholder : ""}
            disabled={isDisabled}
            required={required && tags.length === 0}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-1.5">
      <U2000Label label={label} required={required} htmlFor={fieldName} />

      {inlineEnd != null ? (
        <div className="flex flex-row items-center gap-2">
          <div className="min-w-0 flex-1">{tagInputRow}</div>
          <div className="flex shrink-0 items-center self-center *:h-auto *:min-h-0">{inlineEnd}</div>
        </div>
      ) : (
        tagInputRow
      )}

      <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName={fieldName} />
    </div>
  );
};

export default U2000InputTagBase;
